import { useState } from "react";

import { Select, TextField } from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import StaticField from "../../components/StaticField";
import { QualificationType } from "../../models/CodeBook";
import { ProgramRequestDetail, ProgramRequestData } from "../../models/ProgramRequest";
import {
  useGetEducationSectorsQuery,
  useGetEducationSubSectorsQuery,
  useGetQualificationTypesByProgramQuery
} from "../../queries/codeBookQuery";
import styles from "./ProgramRequestMzo.module.scss";

interface Props {
  programTypeId: number;
  programRequest: ProgramRequestDetail;
  programRequestCorrection: ProgramRequestData;
  onChange: (e: any) => void;
  saveProgramRequest: (e: any) => void;
  errors: any;
}

export function ExpertiseEditProgramInfo({
  programTypeId,
  programRequest,
  programRequestCorrection,
  onChange,
  saveProgramRequest,
  errors
}: Props) {
  const { t } = useTranslation();
  const { data: qualificationTypes } = useGetQualificationTypesByProgramQuery(programTypeId!, {
    skip: !programTypeId,
  });
  const initHkoLevels = (): CodeBook[] => {
    var hkoLevel = [];

    if (programRequestCorrection && programRequestCorrection.qualificationTypeId && programRequestCorrection.qualificationTypeId > 0) {
      if (qualificationTypes) {
        for (let i = 0; i < qualificationTypes.length; i++) {
          if (qualificationTypes[i].id === programRequestCorrection.qualificationTypeId) {
            hkoLevel.push(qualificationTypes[i].hkoLevel);
          }
        }
      }
    }
    return hkoLevel;
  };

  const [hkoLevels, setHkoLevels] = useState<CodeBook[]>(initHkoLevels());
  const { data: educationSectors } = useGetEducationSectorsQuery();
  const { data: educationSubSectors } = useGetEducationSubSectorsQuery(
    programRequestCorrection.educationSectorId!,
    {
      skip: !programRequestCorrection.educationSectorId
    }
  );

  const handleQualificationTypeChange = (event: any) => {
    const qualificationTypeId = event.target.value;
    var hkoLevel = [];

    if (qualificationTypes) {
      for (let i = 0; i < qualificationTypes.length; i++) {
        if (qualificationTypes[i].id === qualificationTypeId) {
          hkoLevel.push(qualificationTypes[i].hkoLevel);
        }
      }
    }

    setHkoLevels(hkoLevel);

    var hkoLevelId = hkoLevel.length === 1 ? hkoLevel[0].id : 0;
    var educationSubSectorId = qualificationTypeId === QualificationType.MicroQualification ? 0 : programRequestCorrection.educationSubSectorId;

    programRequestCorrection.qualificationTypeId = qualificationTypeId;
    programRequestCorrection.hkoLevelId = hkoLevelId;
    programRequestCorrection.educationSubSectorId = educationSubSectorId;

    saveProgramRequest(programRequestCorrection);
  };

  const getQualificationTypesSelectData = () => {
    if (!qualificationTypes) {
      return codeBookToSelectData(undefined);
    } else {
      var distinctQualificationTypes: CodeBook[] = [];

      for (let i = 0; i < qualificationTypes.length; i++) {
        if (!distinctQualificationTypes.find(qt => qt.id === qualificationTypes[i].id)) {
          distinctQualificationTypes.push(qualificationTypes[i]);
        }
      }

      return codeBookToSelectData(distinctQualificationTypes);
    }
  };

  const getHkoLevelName = (): string => {
    var hkoLevelId = hkoLevels.length === 1 ? hkoLevels[0].id : 0;
    return hkoLevelId > 0 && hkoLevels.length === 1 ? hkoLevels[0].name : "";
  }

  return (
    <>
      <Row className={styles.dataRow}>
        <Col md={8}>
          <StaticField
            label={t("request.programNameFreeEntry")}
            value={programRequest.programName}
          />
        </Col>
      </Row>
      <Row className={styles.editRow}>
        <Col md={8}>
          <TextField
            name={nameof<typeof programRequestCorrection>("programName")}
            value={programRequestCorrection?.programName!}
            maxLength={512}
            onChange={onChange}
            errorText={errors?.programName}
          />
        </Col>
      </Row>
      <Row className={styles.dataRow}>
        <Col md={4}>
          <StaticField
            label={t("request.qualification")}
            value={programRequest.qualificationType}
          />
        </Col>
        <Col md={4}>
          <StaticField
            label={t("request.hkoLevel")}
            value={programRequest.hkoLevel}
          />
        </Col>
      </Row>
      <Row className={styles.editRow}>
        <Col md={4}>
          <Select
            name={nameof<typeof programRequestCorrection>("qualificationTypeId")}
            value={programRequestCorrection.qualificationTypeId}
            onChange={handleQualificationTypeChange}
            selectData={getQualificationTypesSelectData()}
          />
        </Col>
        {hkoLevels && hkoLevels.length > 2 &&
          <Col md={4}>
            <Select
              name={nameof<typeof programRequestCorrection>("hkoLevelId")}
              value={programRequestCorrection.hkoLevelId}
              onChange={onChange}
              selectData={codeBookToSelectData(hkoLevels)}
              errorText={errors?.hkoLevelId}
            />
          </Col>
        }
        {(!hkoLevels || hkoLevels.length < 2) && programRequestCorrection.qualificationTypeId !== QualificationType.MicroQualification &&
          <Col md={4}>
            <StaticField
              label={""}
              value={getHkoLevelName()}
            />
          </Col>
        }
      </Row>
      <Row className={styles.dataRow}>
        <Col md={4}>
          <StaticField
            label={t("common.educationSector")}
            value={programRequest.educationSector}
          />
        </Col>
        {programRequest && programRequest.qualificationTypeId !== QualificationType.MicroQualification &&
          <Col md={4}>
            <StaticField
              label={t("common.educationSubSector")}
              value={programRequest.educationSubSector}
            />
          </Col>
        }
      </Row>
      <Row className={styles.editRow}>
        <Col md={4}>
          <Select
            name={nameof<typeof programRequestCorrection>("educationSectorId")}
            value={programRequestCorrection.educationSectorId}
            onChange={onChange}
            selectData={codeBookToSelectData(educationSectors)}
          />
        </Col>
        {programRequest && programRequestCorrection.qualificationTypeId !== QualificationType.MicroQualification &&
          <Col md={4}>
            <Select
              name={nameof<typeof programRequestCorrection>("educationSubSectorId")}
              value={programRequestCorrection.educationSubSectorId}
              onChange={onChange}
              selectData={codeBookToSelectData(educationSubSectors)}
              errorText={errors?.educationSubSectorId}
            />
          </Col>
        }
      </Row>
    </>
  )
}